import {ReactElement, useEffect, useMemo, useState} from "react";
import {PortfolioFinancialTable} from "../../components/portfolio-financial/PortfolioFinancialTable";
import {FinancialEntity} from "../../contexts/chartofaccounts/ChartOfAccountsContext";
import {Property} from "../../contexts/properties/PropertiesContext";
import {useQueryPropertyTagsLazyQuery} from "../../__generated__/generated_types";
import {CustomZDDropdown} from "../../atoms/custom-zd-dropdown/CustomZDDropdown";
import css from "./styles/css.module.scss";

interface PortfolioFinancialTablePageProps {
    snapshotId: string;
    chartOfAccountsTree: FinancialEntity[];
    properties: Property[];
    budgetYear: number;
}

export function PortfolioFinancialTablePage({
    snapshotId,
    chartOfAccountsTree,
    properties,
    budgetYear,
}: PortfolioFinancialTablePageProps): ReactElement {
    const [queryPropertyTags, {data: propertyTagsData}] = useQueryPropertyTagsLazyQuery({fetchPolicy: "no-cache"});
    const [propertyList, setPropertyList] = useState<Property[]>(properties);
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>(properties.map(p => p.id));
    const [selectedPropertyTags, setSelectedPropertyTags] = useState<string[]>([]);

    const selectedProperties = useMemo(
        () => propertyList.filter(p => selectedPropertyIds.includes(p.id)),
        [propertyList, selectedPropertyIds]
    );

    const allPropertyTags = useMemo(() => {
        const all = new Set<string>();
        for(const propertyTags of propertyTagsData?.queryPropertyTags ?? []) {
            for(const tag of propertyTags.tags) {
                all.add(tag);
            }
        }
        return Array.from(all);
    }, [propertyTagsData]);

    useEffect(() => {
        setPropertyList(properties);
        setSelectedPropertyIds(properties.map(p => p.id));
        queryPropertyTags({
            variables: {
                propertyIds: properties.map(p => p.id)
            }
        });
    }, [properties]);

    useEffect(() => {
        if(selectedPropertyTags.length === 0 || !propertyTagsData?.queryPropertyTags.length) {
            setPropertyList(properties);
            return;
        }

        const taggedPropertyIds = new Set(
            propertyTagsData.queryPropertyTags
                .filter(propertyTags => propertyTags.tags.some(tag => selectedPropertyTags.includes(tag)))
                .map(propertyTags => propertyTags.propertyId)
        );
        setPropertyList(properties.filter(property => taggedPropertyIds.has(property.id)));
        setSelectedPropertyIds(current => current.filter(id => taggedPropertyIds.has(id)));
    }, [selectedPropertyTags, propertyTagsData, properties]);

    return <PortfolioFinancialTable
        snapshotId={snapshotId}
        chartOfAccountsTree={chartOfAccountsTree}
        properties={selectedProperties}
        coaFilter={undefined}
        budgetYear={budgetYear}
        propertiesFilter={
            <div className={css.propertyFilterGroup}>
                <CustomZDDropdown
                    initialSelectedItems={selectedPropertyTags}
                    widthSpec="12rem"
                    applySelectedItems={items => setSelectedPropertyTags(items as string[])}
                    openDropdownPlaceholder="Property Tags"
                    closedDropdownPlaceholder="Property Tags"
                    options={allPropertyTags.map(tag => ({value: tag, label: tag}))}
                    isError={undefined}
                    isMulti
                    allOption
                />
                <CustomZDDropdown
                    initialSelectedItems={selectedPropertyIds}
                    widthSpec="12rem"
                    applySelectedItems={items => setSelectedPropertyIds(items as string[])}
                    openDropdownPlaceholder="Properties"
                    closedDropdownPlaceholder="Properties"
                    options={propertyList.map(property => ({value: property.id, label: property.name}))}
                    isError={undefined}
                    isMulti
                    allOption
                />
            </div>
        }
    />;
}
